import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { FAST_TASK_DEFAULTS, MODEL_OPTIONS, PREMIUM_TASK_DEFAULTS, TASK_MODEL_FIELDS } from '../lib/modelCatalog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { useTheme } from 'next-themes';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { ModelBadge } from '../components/design-kit';
import { 
  ChevronLeft,
  Key,
  Save,
  Loader2,
  Eye,
  EyeOff,
  BarChart3,
  DollarSign,
  Clock,
  Zap,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Lightbulb,
  Sparkles,
  Target,
  Brain,
  X,
  Plus,
  Trash2,
  Bell,
  Mail,
  Monitor,
  Moon,
  Sun
} from 'lucide-react';

const modeCards = {
  cheap: {
    title: 'Cheap',
    detail: 'Bias the app toward faster models while keeping premium reasoning where it matters.',
  },
  premium: {
    title: 'Premium',
    detail: 'Use strongest GPT writing/reviewing models plus Grok for multimodal source understanding.',
  },
  custom: {
    title: 'Custom',
    detail: 'Choose the exact model per task surface and manage provider keys directly.',
  },
};

function mapTierToMode(tier) {
  if (tier === 'enterprise') return 'custom';
  if (tier === 'pro') return 'premium';
  return 'cheap';
}

function mapModeToTier(mode) {
  if (mode === 'custom') return 'enterprise';
  if (mode === 'premium') return 'pro';
  return 'free';
}

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { theme, setTheme } = useTheme();
  
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [xaiApiKey, setXaiApiKey] = useState('');
  const [showXaiApiKey, setShowXaiApiKey] = useState(false);
  const [aiMode, setAiMode] = useState(mapTierToMode(user?.ai_tier));
  const [allowAppDefaults, setAllowAppDefaults] = useState(user?.ai_allow_app_defaults !== false);
  const [selectedModel, setSelectedModel] = useState(user?.preferred_model || 'gpt-5.4');
  const [taskModels, setTaskModels] = useState(user?.ai_task_models || PREMIUM_TASK_DEFAULTS);
  const [appearanceMode, setAppearanceMode] = useState(user?.appearance_mode || theme || 'system');
  const [voiceMode, setVoiceMode] = useState(user?.voice_mode || 'browser');
  const [autoFormat, setAutoFormat] = useState(user?.auto_format !== false);
  const [realtimeMode, setRealtimeMode] = useState(user?.realtime_mode || 'text');
  const [autoUpdateNotes, setAutoUpdateNotes] = useState(user?.auto_update_notes || false);
  const [isSaving, setIsSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);
  
  // Learn-assist preferences
  const [learnAssistAutoSuggest, setLearnAssistAutoSuggest] = useState(user?.learn_assist_auto_suggest !== false);
  const [learnAssistSensitivity, setLearnAssistSensitivity] = useState(user?.learn_assist_sensitivity || 'balanced');
  const [learnAssistFocusTopics, setLearnAssistFocusTopics] = useState(user?.learn_assist_focus_topics || []);
  const [learnAssistExcludeTopics, setLearnAssistExcludeTopics] = useState(user?.learn_assist_exclude_topics || []);
  const [newFocusTopic, setNewFocusTopic] = useState('');
  const [newExcludeTopic, setNewExcludeTopic] = useState('');
  
  // Notification preferences
  const [emailNotifications, setEmailNotifications] = useState(user?.email_notifications !== false);
  const [notificationEmail, setNotificationEmail] = useState(user?.notification_email || '');

  useEffect(() => {
    loadUsage();
  }, []);

  useEffect(() => {
    if (!user) return;
    setAiMode(mapTierToMode(user.ai_tier));
    setAllowAppDefaults(user.ai_allow_app_defaults !== false);
    setSelectedModel(user.preferred_model || 'gpt-5.4');
    setTaskModels(user.ai_task_models || PREMIUM_TASK_DEFAULTS);
    setAppearanceMode(user.appearance_mode || 'system');
  }, [user]);

  useEffect(() => {
    if (!theme) return;
    if (!user?.appearance_mode) {
      setAppearanceMode(theme);
    }
  }, [theme, user?.appearance_mode]);

  const loadUsage = async () => {
    try {
      const data = await api.getUsage();
      setUsage(data);
    } catch (error) {
      console.error('Failed to load usage:', error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const resolvedTaskModels =
        aiMode === 'cheap'
          ? FAST_TASK_DEFAULTS
          : aiMode === 'premium'
            ? PREMIUM_TASK_DEFAULTS
            : taskModels;
      const updates = {
        ai_tier: mapModeToTier(aiMode),
        ai_allow_app_defaults: allowAppDefaults,
        preferred_model: selectedModel,
        ai_task_models: resolvedTaskModels,
        appearance_mode: appearanceMode,
        voice_mode: voiceMode,
        auto_format: autoFormat,
        realtime_mode: realtimeMode,
        auto_update_notes: autoUpdateNotes,
        // Learn-assist preferences
        learn_assist_auto_suggest: learnAssistAutoSuggest,
        learn_assist_sensitivity: learnAssistSensitivity,
        learn_assist_focus_topics: learnAssistFocusTopics,
        learn_assist_exclude_topics: learnAssistExcludeTopics,
        // Notification preferences
        email_notifications: emailNotifications,
        notification_email: notificationEmail || null
      };
      
      // Only update API key if it was changed (not masked)
      if (apiKey && !apiKey.includes('•')) {
        updates.openai_api_key = apiKey;
      }
      if (xaiApiKey && !xaiApiKey.includes('•')) {
        updates.xai_api_key = xaiApiKey;
      }

      const result = await api.updateSettings(updates);
      updateUser(result);
      setTheme(result.appearance_mode || appearanceMode);
      toast.success('Settings saved successfully');
      
      // Clear the API key input if it was saved
      if (apiKey && !apiKey.includes('•')) {
        setApiKey('');
      }
      if (xaiApiKey && !xaiApiKey.includes('•')) {
        setXaiApiKey('');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveApiKey = async () => {
    setIsSaving(true);
    try {
      const result = await api.updateSettings({ openai_api_key: '' });
      updateUser(result);
      setApiKey('');
      toast.success('OpenAI key removed');
    } catch (error) {
      toast.error('Failed to remove OpenAI key');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveXaiKey = async () => {
    setIsSaving(true);
    try {
      const result = await api.updateSettings({ xai_api_key: '' });
      updateUser(result);
      setXaiApiKey('');
      toast.success('xAI key removed');
    } catch (error) {
      toast.error('Failed to remove xAI key');
    } finally {
      setIsSaving(false);
    }
  };

  const formatCost = (cost) => {
    if (cost === 0) return '$0.00';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  const updateTaskModel = (taskKey, modelValue) => {
    setTaskModels((prev) => ({ ...prev, [taskKey]: modelValue }));
  };

  const visibleTaskModels =
    aiMode === 'cheap'
      ? FAST_TASK_DEFAULTS
      : aiMode === 'premium'
        ? PREMIUM_TASK_DEFAULTS
        : taskModels;

  const handleAppearanceChange = (value) => {
    setAppearanceMode(value);
    setTheme(value);
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-auto min-h-16 max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground">Settings</span>
          </div>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-xl sm:w-auto"
            data-testid="save-settings-btn"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        <Tabs defaultValue="api" className="space-y-6">
          <TabsList className="grid h-auto w-full grid-cols-2 gap-2 sm:grid-cols-5">
            <TabsTrigger value="api" data-testid="api-tab">API Key</TabsTrigger>
            <TabsTrigger value="preferences" data-testid="preferences-tab">Preferences</TabsTrigger>
            <TabsTrigger value="learn-assist" data-testid="learn-assist-tab">Learn-assist</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="notifications-tab">Notifications</TabsTrigger>
            <TabsTrigger value="usage" data-testid="usage-tab">Usage</TabsTrigger>
          </TabsList>

          {/* API Key Tab */}
          <TabsContent value="api" className="space-y-6">
            <Card className="border-border/50 shadow-card" data-testid="api-key-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  AI Access
                </CardTitle>
                <CardDescription>
                  LectureAI can use app-hosted AI defaults, your own provider keys, or both.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={user?.llm_inference_available ? 'default' : 'secondary'}>
                    {user?.llm_inference_available ? 'Text AI available' : 'Text AI unavailable'}
                  </Badge>
                  <Badge variant={user?.platform_openai_configured ? 'secondary' : 'outline'}>
                    {user?.platform_openai_configured ? 'App OpenAI configured' : 'No app OpenAI key'}
                  </Badge>
                  <Badge variant={user?.platform_xai_configured ? 'secondary' : 'outline'}>
                    {user?.platform_xai_configured ? 'App xAI configured' : 'No app xAI key'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-4">
                  <div>
                    <p className="font-medium">Use LectureAI hosted defaults</p>
                    <p className="text-sm text-muted-foreground">
                      Keeps AI features working without requiring your own key when platform credentials exist.
                    </p>
                  </div>
                  <Switch
                    checked={allowAppDefaults}
                    onCheckedChange={setAllowAppDefaults}
                    data-testid="allow-app-defaults-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Mode Picker
                </CardTitle>
                <CardDescription>
                  Cheap and Premium keep the app simple. Custom lets you pin the default chat model yourself.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  {Object.entries(modeCards).map(([id, option]) => (
                    <label
                      key={id}
                      className={`rounded-xl border-2 p-4 cursor-pointer transition-[border-color,background-color] duration-150 ease-out motion-reduce:transition-none ${
                        aiMode === id ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/40'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ai-mode"
                        className="sr-only"
                        checked={aiMode === id}
                        onChange={() => setAiMode(id)}
                      />
                      <p className="font-medium">{option.title}</p>
                      <p className="mt-2 text-sm text-muted-foreground">{option.detail}</p>
                    </label>
                  ))}
                </div>
                {aiMode === 'custom' && (
                  <div className="space-y-2 rounded-xl border border-border p-4">
                    <Label className="text-sm font-medium">Default text model</Label>
                    <Select value={selectedModel} onValueChange={setSelectedModel}>
                      <SelectTrigger className="h-12" data-testid="model-preference-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODEL_OPTIONS.map((model) => (
                          <SelectItem key={model.value} value={model.value}>
                            <div className="flex flex-col">
                              <span>{model.label}</span>
                              <span className="text-xs text-muted-foreground">{model.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      This currently drives the app&apos;s OpenAI-powered text tasks and review flows.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Provider Keys</CardTitle>
                <CardDescription>
                  Add your own keys if you want BYOK routing across GPT writing/review flows and Grok multimodal reasoning.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label>OpenAI key</Label>
                  <div className="relative">
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      placeholder={user?.has_api_key ? '••••••••••••••••••••••' : 'sk-...'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      className="h-12 pr-10"
                      data-testid="api-key-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showApiKey ? 'Hide OpenAI key' : 'Show OpenAI key'}
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.has_api_key ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-600">Your OpenAI key is configured</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveApiKey}
                          className="ml-auto text-destructive hover:text-destructive"
                          data-testid="remove-api-key-btn"
                        >
                          Remove Key
                        </Button>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-amber-600">No personal OpenAI key configured</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>xAI key</Label>
                  <div className="relative">
                    <Input
                      type={showXaiApiKey ? 'text' : 'password'}
                      placeholder={user?.has_xai_api_key ? '••••••••••••••••••••••' : 'xai-...'}
                      value={xaiApiKey}
                      onChange={(e) => setXaiApiKey(e.target.value)}
                      className="h-12 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowXaiApiKey(!showXaiApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showXaiApiKey ? 'Hide xAI key' : 'Show xAI key'}
                    >
                      {showXaiApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.has_xai_api_key ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-600">Your xAI key is configured</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveXaiKey}
                          className="ml-auto text-destructive hover:text-destructive"
                        >
                          Remove Key
                        </Button>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-amber-500" />
                        <span className="text-sm text-amber-600">No personal xAI key configured</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    xAI keys now power Grok routing for multimodal and long-context subject understanding.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Current Model Stack
                </CardTitle>
                <CardDescription>
                  The strongest models currently wired into the app for writing, multimodal understanding, and realtime voice.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {MODEL_OPTIONS.map((model) => (
                    <div key={model.value} className="flex items-center justify-between gap-4 rounded-xl bg-muted/50 p-3">
                      <div>
                        <p className="font-medium">{model.label}</p>
                        <p className="text-xs text-muted-foreground">{model.description}</p>
                      </div>
                      <ModelBadge modelId={model.value} provider={model.provider} family={model.family} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6">
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Moon className="w-5 h-5" />
                  Appearance
                </CardTitle>
                <CardDescription>
                  Choose a single theme for the whole app or follow your operating system automatically.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 md:grid-cols-3">
                  {[
                    {
                      value: 'light',
                      title: 'Light',
                      detail: 'Bright surfaces for daytime study sessions.',
                      icon: Sun,
                    },
                    {
                      value: 'dark',
                      title: 'Dark',
                      detail: 'Low-glare interface for focused late-night work.',
                      icon: Moon,
                    },
                    {
                      value: 'system',
                      title: 'System',
                      detail: 'Automatically match your device appearance.',
                      icon: Monitor,
                    },
                  ].map((option) => {
                    const Icon = option.icon;
                    const active = appearanceMode === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleAppearanceChange(option.value)}
                        className={`rounded-2xl border-2 p-4 text-left transition-[transform,border-color,background-color] duration-150 ease-out active:scale-[0.98] ${
                          active
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:-translate-y-[1px] hover:border-muted-foreground'
                        }`}
                      >
                        <div className="mb-3 flex items-center gap-3">
                          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{option.title}</p>
                            <p className="text-xs text-muted-foreground">{option.value === 'system' ? 'Recommended default' : 'Manual override'}</p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{option.detail}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Default Model
                </CardTitle>
                <CardDescription>
                  Choose your preferred AI model for lecture formatting and Q&A
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="h-12" data-testid="model-preference-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        <div className="flex flex-col">
                          <span>{model.label}</span>
                          <span className="text-xs text-muted-foreground">{model.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  Task Routing
                </CardTitle>
                <CardDescription>
                  Premium mode uses strong defaults automatically. Switch to Custom if you want to pin exact models for each task.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {TASK_MODEL_FIELDS.map((task) => (
                  <div key={task.key} className="rounded-xl border border-border/70 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium">{task.label}</p>
                        <p className="text-xs text-muted-foreground">{visibleTaskModels[task.key] || PREMIUM_TASK_DEFAULTS[task.key] || 'Default routing'}</p>
                      </div>
                      {visibleTaskModels[task.key] ? (
                        <ModelBadge
                          modelId={visibleTaskModels[task.key]}
                          provider={visibleTaskModels[task.key].startsWith('grok-') ? 'xai' : 'openai'}
                          family={visibleTaskModels[task.key] === 'gpt-realtime' ? 'realtime' : 'reasoning'}
                        />
                      ) : null}
                    </div>
                    <Select
                      value={visibleTaskModels[task.key] || PREMIUM_TASK_DEFAULTS[task.key] || selectedModel}
                      onValueChange={(value) => updateTaskModel(task.key, value)}
                      disabled={aiMode !== 'custom'}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODEL_OPTIONS.map((model) => (
                          <SelectItem key={`${task.key}-${model.value}`} value={model.value}>
                            {model.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Voice Input Mode</CardTitle>
                <CardDescription>
                  Choose how you want to input voice in Q&A mode
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    voiceMode === 'browser' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => setVoiceMode('browser')}
                  data-testid="browser-voice-option"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">Browser Speech Recognition</span>
                    <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded-full">
                      Free
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Uses your browser's built-in speech recognition. Works offline and doesn't cost anything.
                  </p>
                </div>

                <div
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                    voiceMode === 'whisper' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => setVoiceMode('whisper')}
                  data-testid="whisper-voice-option"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">OpenAI Whisper</span>
                    <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-1 rounded-full">
                      $0.006/min
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Higher accuracy transcription using OpenAI's Whisper model. Requires API key.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Auto-Format Setting */}
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Auto-Format Lectures</CardTitle>
                <CardDescription>
                  Automatically transcribe and format notes when you end a lecture recording
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                  <div>
                    <p className="font-medium">Auto-transcribe and format</p>
                    <p className="text-sm text-muted-foreground">
                      When enabled, your lecture will be automatically transcribed and formatted into structured notes when you click "End Lecture"
                    </p>
                  </div>
                  <Switch
                    checked={autoFormat}
                    onCheckedChange={setAutoFormat}
                    data-testid="auto-format-toggle"
                  />
                </div>
                {autoFormat && (
                  <p className="mt-3 text-xs text-primary flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Auto-format is ON - Lectures will be processed automatically
                  </p>
                )}
              </CardContent>
            </Card>

            {/* AI Tutor Settings */}
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  AI Tutor ("I'm Confused")
                </CardTitle>
                <CardDescription>
                  Configure how the AI tutor helps you understand lecture material
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Realtime Mode */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Interaction Mode</Label>
                  <div className="space-y-2">
                    <div
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        realtimeMode === 'text' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      onClick={() => setRealtimeMode('text')}
                      data-testid="realtime-text-option"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Text Chat
                        </span>
                        <span className="text-xs bg-green-500/10 text-green-600 px-2 py-1 rounded-full">
                          Recommended
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Type your questions and get text responses. Best for detailed explanations.
                      </p>
                    </div>

                    <div
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        realtimeMode === 'voice' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      onClick={() => setRealtimeMode('voice')}
                      data-testid="realtime-voice-option"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">Voice-to-Voice</span>
                        <span className="text-xs bg-amber-500/10 text-amber-600 px-2 py-1 rounded-full">
                          Uses more credits
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Speak your questions and hear responses. More natural but uses Realtime API.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Auto-update Notes */}
                <div className="flex items-center justify-between p-4 rounded-xl border border-border">
                  <div>
                    <p className="font-medium">Auto-update notes from conversations</p>
                    <p className="text-sm text-muted-foreground">
                      When the AI suggests clarifications, automatically add them to your notes
                    </p>
                  </div>
                  <Switch
                    checked={autoUpdateNotes}
                    onCheckedChange={setAutoUpdateNotes}
                    data-testid="auto-update-notes-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Learn-assist Tab */}
          <TabsContent value="learn-assist" className="space-y-6">
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Learn-assist™ Preferences
                </CardTitle>
                <CardDescription>
                  Customize how Learn-assist generates personalized learning recommendations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Auto-suggest toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-suggest" className="text-base">Personalized Recommendations</Label>
                    <p className="text-sm text-muted-foreground">
                      Automatically suggest topics based on your review performance
                    </p>
                  </div>
                  <Switch
                    id="auto-suggest"
                    checked={learnAssistAutoSuggest}
                    onCheckedChange={setLearnAssistAutoSuggest}
                  />
                </div>
                
                <div className="border-t border-border pt-6">
                  {/* Sensitivity setting */}
                  <div className="space-y-3">
                    <div className="space-y-0.5">
                      <Label className="text-base">Recommendation Sensitivity</Label>
                      <p className="text-sm text-muted-foreground">
                        How many wrong answers trigger a topic recommendation
                      </p>
                    </div>
                    <Select value={learnAssistSensitivity} onValueChange={setLearnAssistSensitivity}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aggressive">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-red-500" />
                            <span>Aggressive</span>
                            <span className="text-xs text-muted-foreground ml-2">2+ wrong = recommend</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="balanced">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-amber-500" />
                            <span>Balanced</span>
                            <span className="text-xs text-muted-foreground ml-2">40% wrong = recommend</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="conservative">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-green-500" />
                            <span>Conservative</span>
                            <span className="text-xs text-muted-foreground ml-2">50% wrong = recommend</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  {/* Focus topics */}
                  <div className="space-y-3">
                    <div className="space-y-0.5">
                      <Label className="text-base flex items-center gap-2">
                        <Brain className="w-4 h-4 text-primary" />
                        Focus Topics
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Topics you want to prioritize in recommendations (always show)
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {learnAssistFocusTopics.map((topic, i) => (
                        <Badge key={i} variant="default" className="flex items-center gap-1">
                          {topic}
                          <button 
                            onClick={() => setLearnAssistFocusTopics(prev => prev.filter((_, idx) => idx !== i))}
                            className="ml-1 hover:bg-primary-foreground/20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a focus topic..."
                        value={newFocusTopic}
                        onChange={(e) => setNewFocusTopic(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newFocusTopic.trim()) {
                            e.preventDefault();
                            setLearnAssistFocusTopics(prev => [...prev, newFocusTopic.trim()]);
                            setNewFocusTopic('');
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (newFocusTopic.trim()) {
                            setLearnAssistFocusTopics(prev => [...prev, newFocusTopic.trim()]);
                            setNewFocusTopic('');
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  {/* Exclude topics */}
                  <div className="space-y-3">
                    <div className="space-y-0.5">
                      <Label className="text-base flex items-center gap-2">
                        <Trash2 className="w-4 h-4 text-muted-foreground" />
                        Excluded Topics
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Topics to ignore in recommendations (never show)
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {learnAssistExcludeTopics.map((topic, i) => (
                        <Badge key={i} variant="secondary" className="flex items-center gap-1">
                          {topic}
                          <button 
                            onClick={() => setLearnAssistExcludeTopics(prev => prev.filter((_, idx) => idx !== i))}
                            className="ml-1 hover:bg-muted-foreground/20 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a topic to exclude..."
                        value={newExcludeTopic}
                        onChange={(e) => setNewExcludeTopic(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newExcludeTopic.trim()) {
                            e.preventDefault();
                            setLearnAssistExcludeTopics(prev => [...prev, newExcludeTopic.trim()]);
                            setNewExcludeTopic('');
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (newExcludeTopic.trim()) {
                            setLearnAssistExcludeTopics(prev => [...prev, newExcludeTopic.trim()]);
                            setNewExcludeTopic('');
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <p className="text-sm text-muted-foreground">
                      <strong>How it works:</strong> Learn-assist analyzes your review session performance 
                      and confusion logs to identify topics where you need help. Recommendations are based 
                      on your correct/incorrect answer ratio and frequently confused topics.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Email Notifications
                </CardTitle>
                <CardDescription>
                  Manage email notifications for exam reminders and study recommendations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="email-notifications" className="text-base">Enable Email Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive reminders about exams and study suggestions
                    </p>
                  </div>
                  <Switch
                    id="email-notifications"
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>
                
                {emailNotifications && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="space-y-2">
                      <Label htmlFor="notification-email">Notification Email (optional)</Label>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <Input
                          id="notification-email"
                          type="email"
                          placeholder={user?.email || 'your@email.com'}
                          value={notificationEmail}
                          onChange={(e) => setNotificationEmail(e.target.value)}
                          className="flex-1"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Leave empty to use your account email ({user?.email})
                      </p>
                    </div>
                    
                    <div className="rounded-lg bg-muted/50 p-4 space-y-3">
                      <h4 className="font-medium text-sm">You'll receive emails for:</h4>
                      <ul className="text-sm text-muted-foreground space-y-2">
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          Weekly reminders to input exam scores
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          Upcoming exam reminders (7, 3, and 1 day before)
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          Study recommendations based on your progress
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Usage Tab */}
          <TabsContent value="usage" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-border/50 shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {formatCost(usage?.total_cost || 0)}
                      </p>
                      <p className="text-sm text-muted-foreground">Total Spent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                      <Clock className="w-5 h-5 text-secondary-foreground" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {Math.floor((usage?.total_audio_seconds || 0) / 60)}m
                      </p>
                      <p className="text-sm text-muted-foreground">Audio Transcribed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-accent-foreground" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {((usage?.total_tokens_input || 0) + (usage?.total_tokens_output || 0)).toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">Tokens Used</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Usage by Model */}
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Usage by Model
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsage ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : Object.keys(usage?.by_model || {}).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No usage data yet. Start using the app to see statistics.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(usage?.by_model || {}).map(([model, data]) => (
                      <div 
                        key={model}
                        className="flex items-center justify-between p-3 rounded-xl bg-muted/50"
                      >
                        <div>
                          <p className="font-medium">{model}</p>
                          <p className="text-xs text-muted-foreground">{data.count} requests</p>
                        </div>
                        <span className="font-medium">{formatCost(data.cost)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Usage by Operation */}
            <Card className="border-border/50 shadow-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Usage by Operation</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingUsage ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : Object.keys(usage?.by_operation || {}).length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No usage data yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(usage?.by_operation || {}).map(([operation, data]) => (
                      <div 
                        key={operation}
                        className="flex items-center justify-between p-3 rounded-xl bg-muted/50"
                      >
                        <div>
                          <p className="font-medium capitalize">{operation}</p>
                          <p className="text-xs text-muted-foreground">{data.count} requests</p>
                        </div>
                        <span className="font-medium">{formatCost(data.cost)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
